import React from 'react'

const ensureArray = <T>(value: T | T[]): T[] => {
  return Array.isArray(value) ? value : [value]
}

const isNotNil = <T>(value: T | null): value is T => value !== null

const useClickOutside = <T extends HTMLElement>(
  refs: React.RefObject<T | null> | React.RefObject<T | null>[],
  handler: (event: MouseEvent) => void,
  enabled = true
) => {
  const [enabledDone, setEnabledDone] = React.useState(enabled)

  const handlerRef = React.useRef(handler)
  handlerRef.current = handler

  const refsArray = ensureArray(refs)

  const refElements = React.useRef<T[]>([])
  refElements.current = refsArray.map(ref => ref.current).filter(isNotNil)

  React.useEffect(() => {
    if (!enabledDone) {
      return undefined
    }

    const listener = (event: MouseEvent) => {
      if (refElements.current.some(element => element.contains(event.target as Node))) {
        return
      }

      handlerRef.current?.(event)
    }

    // Capture is important here because we want to check this before any other handlers can trigger a rerender.
    window.addEventListener('click', listener, true)

    return () => {
      window.removeEventListener('click', listener, true)
    }
  }, [enabledDone])

  // We postpone activation of the click outside handler to the next render cycle to avoid
  // the handler being called on the same click event that enabled it.
  React.useEffect(() => {
    if (enabled !== enabledDone) {
      setEnabledDone(enabled)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])
}

export default useClickOutside
